import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Parse a "YYYY-MM-DDTHH:mm" local datetime string (the native
// datetime-local input contract) into a local Date.
function parseLocal(value) {
  const m = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return isNaN(d.getTime()) ? undefined : d;
}

function toLocalString(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toDisplay(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Calendar-driven replacement for <input type="datetime-local">.
// value/onChange use the same "YYYY-MM-DDTHH:mm" string contract as the native input.
export function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date & time",
  disabled,
  className,
  style,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseLocal(value);
  const timeValue = selected
    ? `${String(selected.getHours()).padStart(2, "0")}:${String(selected.getMinutes()).padStart(2, "0")}`
    : "00:00";

  const commit = (date, time) => {
    const [h, m] = time.split(":").map(Number);
    const next = new Date(date);
    next.setHours(h, m, 0, 0);
    onChange(toLocalString(next));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          style={style}
          className={cn(
            "df-input-rect flex items-center justify-between gap-2 text-left",
            !selected && "text-white/40",
            className
          )}
        >
          <span>{selected ? toDisplay(selected) : placeholder}</span>
          <CalendarIcon size={16} className="opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => date && commit(date, timeValue)}
          initialFocus
        />
        <div className="flex items-center gap-2 border-t border-border p-3">
          <span className="text-xs text-muted-foreground shrink-0">Time</span>
          <input
            type="time"
            value={timeValue}
            onChange={(e) => commit(selected || new Date(), e.target.value)}
            className="df-input-rect !py-1.5 !text-sm flex-1"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
